import type { DocFieldSource, EvidenceSection } from '../../types/siteEvidence';

/** Closed nested containers: evidence values, never source metadata or sensitive locations. */
const NESTED_FIELDS: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  grid: { capacityDetail: ['mw', 'basis', 'enteredVia'] },
  waterResource: {
    municipalSupply: ['provider', 'sourceDescription', 'avgMgd', 'peakMgd', 'permittedMgd', 'excessMgd', 'storageGallons', 'nearestMainMiles', 'lineToSite', 'connectionType', 'notes'],
    wastewaterService: ['provider', 'sourceDescription', 'avgMgd', 'peakMgd', 'permittedMgd', 'excessMgd', 'storageGallons', 'nearestMainMiles', 'lineToSite', 'connectionType', 'notes'],
  },
  geotechnical: {
    professionalFacts: ['preparedBy', 'reportDate', 'bearingCapacityKPa', 'soilClassification', 'boreholeDepthM', 'groundwaterDepthM', 'foundationRecommendation', 'settlementRisk', 'liquefactionRisk', 'expansiveSoilRisk', 'seismicSiteClass'],
  },
  mineralResources: {
    titleFacts: ['preparer', 'reportDate', 'countyRecordReference', 'ownershipStatus', 'reservedCommodities', 'activeLeases', 'surfaceUseRestrictions'],
  },
  culturalResources: {
    // Public summary counts/distances only; property identity/location arrays stay withheld.
    publicRegistryScreen: ['listedPropertiesOnParcel', 'listedPropertiesWithin1Mi', 'listedPropertiesWithin5Mi', 'nearestDistanceMi', 'queriedFeatureCount', 'truncated', 'sourceVintage', 'screeningOnly', 'preciseLocationsWithheld'],
    professionalSurvey: ['qualifiedProfessional', 'surveyDate', 'surveyLevel', 'acreageSurveyed', 'resourcesIdentified', 'resourceCount', 'eligibilityRecommendation', 'effectFinding', 'shpoCaseNumber', 'sensitiveLocationWithheld'],
  },
};

/** Add admitted nested leaves to existing flat rows without changing their values. */
export function documentFieldEntries(domain: string, data: Record<string, unknown>): Array<[string, unknown]> {
  const out: Array<[string, unknown]> = [];
  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const fields = NESTED_FIELDS[domain]?.[key];
      if (!fields) continue;
      const nested = value as Record<string, unknown>;
      for (const field of fields) if (Object.prototype.hasOwnProperty.call(nested, field)) out.push([`${key}.${field}`, nested[field]]);
    } else if (!Array.isArray(value) || value.every(item => item == null || ['string', 'number', 'boolean'].includes(typeof item))) {
      out.push([key, value]);
    }
  }
  return out;
}

export function valueAtDocumentField(data: unknown, path: string): unknown {
  let value = data;
  for (const key of path.split('.')) {
    if (['__proto__', 'prototype', 'constructor'].includes(key) || !value || typeof value !== 'object' || Array.isArray(value)
      || !Object.prototype.hasOwnProperty.call(value, key)) return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

/** A retained source describes the current adopted leaf only while its exact value matches. */
export function currentDocumentFieldSource(slot: { data?: unknown; docFieldSources?: EvidenceSection<unknown>['docFieldSources'] }, path: string): DocFieldSource | undefined {
  const source = slot.docFieldSources?.[path];
  const value = valueAtDocumentField(slot.data, path);
  return source && ['string', 'number', 'boolean'].includes(typeof value) && source.value === value
    && (typeof value !== 'number' || Number.isFinite(value)) ? source : undefined;
}

/** Units follow the existing typed SED contracts; absent units stay absent. */
export function unitForDocumentField(path: string): string | undefined {
  if (path === 'capacityDetail.mw') return 'MW';
  const leaf = path.split('.').at(-1)!;
  return ({ avgMgd: 'MGD', peakMgd: 'MGD', permittedMgd: 'MGD', excessMgd: 'MGD', storageGallons: 'US gal', nearestMainMiles: 'mi',
    consumptiveUseAfy: 'acre-ft/yr', aquiferDepthToWaterFt: 'ft', surveyedAcreage: 'acres', contourIntervalFt: 'ft',
    bearingCapacityKPa: 'kPa', presumptiveBearingPressureKPa: 'kPa', boreholeDepthM: 'm', groundwaterDepthM: 'm',
    depthToBedrockM: 'm', seasonalWaterTableDepthM: 'm', acreageSurveyed: 'acres' } as Readonly<Record<string, string>>)[leaf];
}
